import type { AutocompleteData } from "@color-finder/color-finder-api/models";
import CircleIcon from "@mui/icons-material/Circle";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  type AutocompleteInputChangeReason,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  type PaperProps,
  TextField,
  Typography,
} from "@mui/material";
import { NoData } from "components/dataDisplay/NoData";
import { useAppConfig } from "config/app-config";
import { usePaginatedAutocompleteOptions } from "hooks/autocomplete-data";
import type { FilterEntry, FilterType } from "hooks/filters";
import { useLatch } from "hooks/latch";
import { useDebounce } from "hooks/utils/debounce";
import type React from "react";
import type { ReactNode } from "react";
import {
  createContext,
  type Dispatch,
  type FC,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { labToCss } from "service/color-math";
import type { NamedColor } from "./ColorFilter";

export interface OemColorCodeFilterProps {
  onChange: Dispatch<NamedColor | undefined>;
  value?: NamedColor;
  oemName: Array<AutocompleteData> | undefined;
}

const PAGE_SIZE = 100;
const SCROLL_LOAD_THRESHOLD_PX = 50;

const PaperFooterContext = createContext<ReactNode>(null);

// Footer sits outside the listbox so appending a page never changes the options MUI uses to reset scroll.
const PaperWithFooter = forwardRef<HTMLDivElement, PaperProps>(
  function PaperWithFooter({ children, ...other }, ref) {
    const footer = useContext(PaperFooterContext);
    return (
      <Paper {...other} ref={ref}>
        {children}
        {footer && (
          // Without this, mousedown on the footer blurs the input and closes the popup before click fires.
          <Box onMouseDown={(event) => event.preventDefault()}>{footer}</Box>
        )}
      </Paper>
    );
  },
);

export const OemColorCodeFilter: FC<OemColorCodeFilterProps> = ({
  value,
  onChange,
  oemName,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wasOpen = useLatch(open);
  const [query, setQuery] = useState<string>();
  const [appConfig] = useAppConfig();
  const debouncedQuery = useDebounce(
    query,
    appConfig.ux.general.inputDebounceTimeMs,
  );

  const filters: Array<FilterEntry> = useMemo(
    () =>
      [
        {
          type: "oemName" as FilterType,
          value: oemName,
        },
      ].filter((e) => e.value !== undefined && e.value.length > 0),
    [oemName],
  );

  const helperText = oemName?.length
    ? t("filter_color_code_oem_helper", {
        oems: oemName.map((n) => n.name).join(", "),
      })
    : undefined;

  const { options, error, isLoading, hasMore, loadMore, retry } =
    usePaginatedAutocompleteOptions({
      entityType: "oemColorCode",
      query: debouncedQuery,
      pageSize: PAGE_SIZE,
      shouldLoad: wasOpen,
      filters,
    });
  const hasOptions = options.length > 0;

  let paperFooter: ReactNode = null;
  if (isLoading && hasOptions) {
    paperFooter = (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          py: "var(--spacing8, 8px)",
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  } else if (error && hasOptions) {
    paperFooter = (
      <Alert
        severity="error"
        action={
          <Button size="small" color="inherit" onClick={retry}>
            {t("filter_retry")}
          </Button>
        }
      >
        <AlertTitle>{t("error_unexpected_title")}</AlertTitle>
        {error.message}
      </Alert>
    );
  }

  // MUI's effect resets the open listbox to the top on every re-render; this one runs after it and restores the position.
  const lastScrollRef = useRef<{ node: HTMLElement; scrollTop: number }>();
  const previousOptionsRef = useRef(options);
  useEffect(() => {
    const previous = previousOptionsRef.current;
    previousOptionsRef.current = options;
    const isSameOrAppended =
      previous === options ||
      (options.length >= previous.length &&
        previous.every((option, index) => option === options[index]));
    const lastScroll = lastScrollRef.current;
    if (!isSameOrAppended) {
      lastScrollRef.current = undefined;
    } else if (lastScroll && lastScroll.node.scrollTop === 0) {
      lastScroll.node.scrollTop = lastScroll.scrollTop;
    }
  });

  const onListboxScroll = (event: React.UIEvent<HTMLElement>) => {
    const { currentTarget: node } = event;
    const { scrollTop, clientHeight, scrollHeight } = node;
    lastScrollRef.current = { node, scrollTop };
    const isNearBottom =
      scrollTop + clientHeight >= scrollHeight - SCROLL_LOAD_THRESHOLD_PX;
    if (hasMore && isNearBottom) {
      loadMore();
    }
  };

  // A new object per render makes MUI reset the typed text to the selected label on every re-render.
  const selectedOption = useMemo<AutocompleteData | null>(
    () =>
      value
        ? {
            name: value.name,
            groupName: undefined,
            value: "",
            cieLab: undefined,
          }
        : null,
    [value],
  );

  const formatOptionLabel = (option: AutocompleteData) =>
    [option.groupName, option.value, option.name].filter((v) => v).join(" / ");

  // Only typed text narrows the list. Any other reason (selection, blur, clear) means
  // the input shows a label or nothing, which must not be sent as a query.
  const onInputChange = (
    _event: React.SyntheticEvent,
    text: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    setQuery(reason === "input" && text ? text : undefined);
  };

  const onValueChange = (
    _event: React.SyntheticEvent,
    option: AutocompleteData | null,
  ) => {
    const namedColor: NamedColor | undefined = option
      ? {
          value: option.cieLab!,
          name: formatOptionLabel(option),
        }
      : undefined;
    onChange(namedColor);
  };

  return (
    <Grid size={{ xs: 12, md: 6, xl: 3 }}>
      <PaperFooterContext.Provider value={paperFooter}>
        <Autocomplete
          fullWidth
          value={selectedOption}
          renderInput={(params) => (
            <TextField
              {...params}
              helperText={helperText}
              label={t("filter_color_code_oem")}
              inputProps={{
                ...params.inputProps,
                style: { minWidth: 0 },
              }}
            />
          )}
          renderOption={(props, option: AutocompleteData, _state) => (
            <Box component="li" {...props}>
              {option.cieLab && (
                <CircleIcon
                  sx={{ color: labToCss(option.cieLab), marginRight: "10px" }}
                />
              )}
              {option.value ?? <NoData label={t("no_code")} />}
              <Typography ml={0.5} mr={0.5}>
                /
              </Typography>
              {option.name ?? <NoData label={t("no_name")} />}
            </Box>
          )}
          loading={isLoading && !hasOptions}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={onValueChange}
          onInputChange={onInputChange}
          options={options}
          groupBy={(val) => val.groupName ?? ""}
          filterOptions={(o) => o}
          slots={{ paper: PaperWithFooter }}
          slotProps={{ listbox: { onScroll: onListboxScroll } }}
          getOptionLabel={(option) => formatOptionLabel(option)}
          isOptionEqualToValue={(option, value) =>
            option.name === value.name &&
            option.value === value.value &&
            option.groupName === value.groupName
          }
          noOptionsText={
            error ? (
              <Alert sx={{ mx: -2, my: -1.8 }} severity="error">
                <AlertTitle>{t("error_unexpected_title")}</AlertTitle>
                {error.message}
              </Alert>
            ) : debouncedQuery ? (
              t("filter_color_code_oem_no_results", { query: debouncedQuery })
            ) : undefined
          }
        />
      </PaperFooterContext.Provider>
    </Grid>
  );
};
